import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from "randmarcomps";
import { Download, ExternalLink, File, FileArchive, FileImage, FileText, Loader2, RefreshCw, Trash2, UploadCloud } from "lucide-react";
import type { AttachmentHeader } from "randmar-api-client";
import {
  randmarApiWithTags,
  useDeleteV4PartnerByApplicationIdAttachmentAndAttachmentUuidMutation,
  usePostV4PartnerByApplicationIdAttachmentUploadMutation,
  usePutV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuAttachmentMutation,
} from "randmar-api-client";
import React from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../new-store/store";

interface ProductAttachmentsProps {
  applicationId: string;
  randmarSKU: string;
  attachments?: AttachmentHeader[];
  loading?: boolean;
  onRefresh: () => Promise<void> | void;
}

type AttachmentAction = "download" | "preview";

const ACCEPTED_ATTACHMENT_TYPES = [
  "application/pdf",
  "image/*",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".txt",
  ".zip",
];

function getAttachmentUuid(attachment: AttachmentHeader) {
  return attachment.AttachmentUUID?.trim() || "";
}

function getAttachmentName(attachment: AttachmentHeader) {
  return attachment.OriginalName?.trim() || "Untitled attachment";
}

function getFileExtension(fileName: string) {
  const match = fileName.match(/\.([^.]+)$/);
  return match?.[1]?.toUpperCase() || "FILE";
}

function formatFileSize(sizeBytes?: number) {
  if (!sizeBytes || sizeBytes <= 0) return "Size unavailable";
  const units = ["B", "KB", "MB", "GB"];
  let size = sizeBytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toLocaleString(undefined, { maximumFractionDigits: unitIndex === 0 ? 0 : 1 })} ${units[unitIndex]}`;
}

function formatCreatedAt(createdAt?: string) {
  if (!createdAt) return "Date unavailable";
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function getAttachmentIcon(contentType?: string | null, fileName?: string) {
  const normalizedType = contentType?.toLowerCase() ?? "";
  const normalizedName = fileName?.toLowerCase() ?? "";

  if (normalizedType.includes("pdf") || normalizedName.endsWith(".pdf")) return FileText;
  if (normalizedType.startsWith("image/")) return FileImage;
  if (normalizedType.includes("zip") || normalizedName.endsWith(".zip")) return FileArchive;
  return File;
}

function canPreviewAttachment(attachment: AttachmentHeader) {
  const contentType = attachment.ContentType?.toLowerCase() ?? "";
  const fileName = getAttachmentName(attachment).toLowerCase();
  return contentType === "application/pdf" || contentType.startsWith("image/") || fileName.endsWith(".pdf");
}

export default function ProductAttachments({
  applicationId,
  randmarSKU,
  attachments,
  loading,
  onRefresh,
}: ProductAttachmentsProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  const fileInputId = React.useId();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [uploadingCount, setUploadingCount] = React.useState(0);
  const [activeAttachmentUuid, setActiveAttachmentUuid] = React.useState<string | null>(null);
  const [activeDownload, setActiveDownload] = React.useState<{ uuid: string; action: AttachmentAction } | null>(null);

  const [uploadAttachment] = usePostV4PartnerByApplicationIdAttachmentUploadMutation();
  const [registerProductAttachment] = usePutV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuAttachmentMutation();
  const [deleteAttachment] = useDeleteV4PartnerByApplicationIdAttachmentAndAttachmentUuidMutation();

  const attachmentList = React.useMemo(
    () => [...(attachments ?? [])].filter(getAttachmentUuid).sort((a, b) => {
      const dateA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
      const dateB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
      return dateB - dateA;
    }),
    [attachments],
  );
  const isUploading = uploadingCount > 0;
  const isMutating = isUploading || Boolean(activeAttachmentUuid) || Boolean(activeDownload);

  const openFilePicker = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadFiles = React.useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      if (!applicationId || !randmarSKU) {
        toast({ variant: "destructive", title: "Upload unavailable", description: "Missing manufacturer or SKU context." });
        return;
      }

      setUploadingCount(files.length);
      try {
        for (const file of files) {
          const formData = new FormData();
          formData.append("file", file);
          const storageKey = await uploadAttachment({
            applicationId,
            body: formData as unknown as { file: Blob },
          }).unwrap();

          await registerProductAttachment({
            routeManufacturerId: applicationId,
            randmarSku: randmarSKU,
            randmarApiControllersV4ManufacturerProductControllerRegisterProductAttachmentRequest: {
              StorageKey: storageKey,
            },
          }).unwrap();

          setUploadingCount((current) => Math.max(current - 1, 0));
        }

        toast({
          variant: "success",
          title: files.length === 1 ? "Attachment added" : "Attachments added",
          description: files.length === 1 ? `${files[0].name} is now attached to this product.` : `${files.length} files are now attached to this product.`,
        });
        await onRefresh();
      } catch (error) {
        console.error("Failed to upload product attachment:", error);
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: "One or more files could not be attached. Please try again.",
        });
        await onRefresh();
      } finally {
        setUploadingCount(0);
      }
    },
    [applicationId, onRefresh, randmarSKU, registerProductAttachment, toast, uploadAttachment],
  );

  const handleFileChange = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(event.target.files ?? []);
      event.target.value = "";
      await uploadFiles(selectedFiles);
    },
    [uploadFiles],
  );

  const handleDrop = React.useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragActive(false);
      await uploadFiles(Array.from(event.dataTransfer.files ?? []));
    },
    [uploadFiles],
  );

  const getAttachmentBlob = React.useCallback(
    async (attachmentUuid: string) => {
      const result = await dispatch(
        randmarApiWithTags.endpoints.getV4PartnerByApplicationIdAttachmentAndAttachmentUuid.initiate(
          { applicationId, attachmentUuid },
          { subscribe: false, forceRefetch: true },
        ),
      ).unwrap();
      return result;
    },
    [applicationId, dispatch],
  );

  const handleAttachmentAction = React.useCallback(
    async (attachment: AttachmentHeader, action: AttachmentAction) => {
      const attachmentUuid = getAttachmentUuid(attachment);
      if (!attachmentUuid) return;
      setActiveDownload({ uuid: attachmentUuid, action });
      try {
        const blob = await getAttachmentBlob(attachmentUuid);
        const url = URL.createObjectURL(blob);
        const fileName = getAttachmentName(attachment);

        if (action === "preview") {
          window.open(url, "_blank", "noopener,noreferrer");
          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
          return;
        }

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Failed to retrieve product attachment:", error);
        toast({
          variant: "destructive",
          title: action === "preview" ? "Preview failed" : "Download failed",
          description: "The attachment could not be retrieved. Please try again.",
        });
      } finally {
        setActiveDownload(null);
      }
    },
    [getAttachmentBlob, toast],
  );

  const handleDelete = React.useCallback(
    async (attachment: AttachmentHeader) => {
      const attachmentUuid = getAttachmentUuid(attachment);
      if (!attachmentUuid) return;
      setActiveAttachmentUuid(attachmentUuid);
      try {
        await deleteAttachment({ applicationId, attachmentUuid }).unwrap();
        toast({ variant: "success", title: "Attachment removed", description: `${getAttachmentName(attachment)} was removed from this product.` });
        await onRefresh();
      } catch (error) {
        console.error("Failed to delete product attachment:", error);
        toast({
          variant: "destructive",
          title: "Remove failed",
          description: "The attachment could not be removed. Please try again.",
        });
      } finally {
        setActiveAttachmentUuid(null);
      }
    },
    [applicationId, deleteAttachment, onRefresh, toast],
  );

  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm" aria-label="Product attachments" aria-busy={isMutating || loading}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Resources</p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h2 className="text-[24px] font-semibold text-slate-900">Attached files</h2>
            <Badge variant="secondary" size="sm">
              {attachmentList.length} {attachmentList.length === 1 ? "file" : "files"}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="h-10 gap-2 rounded-xl" onClick={() => void onRefresh()} disabled={isMutating || loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            Refresh
          </Button>
          <Button type="button" className="h-10 gap-2 rounded-xl" onClick={openFilePicker} disabled={isMutating || !randmarSKU}>
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <UploadCloud className="h-4 w-4" aria-hidden="true" />}
            {isUploading ? `Uploading ${uploadingCount}` : "Add files"}
          </Button>
        </div>
      </div>

      <input
        id={fileInputId}
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_ATTACHMENT_TYPES.join(",")}
        multiple
        className="hidden"
        onChange={handleFileChange}
        disabled={isMutating}
      />

      <div
        className={`mt-5 rounded-xl border border-dashed px-5 py-5 transition-colors ${
          isDragActive ? "border-[var(--ops-accent)] bg-[var(--ops-accent-muted)]" : "border-slate-200 bg-slate-50/70"
        }`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={handleDrop}
      >
        <button
          type="button"
          className="flex w-full flex-col items-center justify-center gap-2 rounded-lg px-4 py-4 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)] sm:flex-row sm:justify-between sm:text-left"
          onClick={openFilePicker}
          disabled={isMutating}
          aria-describedby={`${fileInputId}-hint`}
        >
          <span className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-[var(--ops-accent)] shadow-sm">
              <UploadCloud className="h-5 w-5" aria-hidden="true" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-slate-900">Drop files here or browse</span>
              <span id={`${fileInputId}-hint`} className="block text-sm text-slate-500">
                Datasheets, manuals, images, spreadsheets, and support documents.
              </span>
            </span>
          </span>
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Multi-file upload</span>
        </button>
      </div>

      <div className="mt-5">
        {attachmentList.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 px-6 py-10 text-center">
            <FileText className="h-10 w-10 text-slate-400" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-slate-900">No files attached</p>
              <p className="mt-1 text-sm text-slate-500">Add datasheets, manuals, certificates, or other buyer-facing product resources.</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-gray-100">
            {attachmentList.map((attachment) => {
              const attachmentUuid = getAttachmentUuid(attachment);
              const fileName = getAttachmentName(attachment);
              const Icon = getAttachmentIcon(attachment.ContentType, fileName);
              const previewable = canPreviewAttachment(attachment);
              const rowBusy = activeAttachmentUuid === attachmentUuid;
              const downloading = activeDownload?.uuid === attachmentUuid;

              return (
                <div key={attachmentUuid} className="flex flex-col gap-4 bg-white p-4 transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="max-w-full truncate text-sm font-semibold text-slate-900" title={fileName}>
                          {fileName}
                        </p>
                        <Badge variant="outline" size="sm">
                          {getFileExtension(fileName)}
                        </Badge>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        {formatFileSize(attachment.SizeBytes)} <span aria-hidden="true">&middot;</span> Added {formatCreatedAt(attachment.CreatedAt)}
                      </p>
                    </div>
                  </div>

                  <TooltipProvider>
                    <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                      {previewable ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 rounded-xl"
                              onClick={() => void handleAttachmentAction(attachment, "preview")}
                              disabled={isMutating}
                              aria-label={`Preview ${fileName}`}
                            >
                              {downloading && activeDownload?.action === "preview" ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                              ) : (
                                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Preview</TooltipContent>
                        </Tooltip>
                      ) : null}

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 rounded-xl"
                            onClick={() => void handleAttachmentAction(attachment, "download")}
                            disabled={isMutating}
                            aria-label={`Download ${fileName}`}
                          >
                            {downloading && activeDownload?.action === "download" ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            ) : (
                              <Download className="h-4 w-4" aria-hidden="true" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Download</TooltipContent>
                      </Tooltip>

                      <AlertDialog>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertDialogTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 rounded-xl"
                                disabled={isMutating}
                                aria-label={`Remove ${fileName}`}
                              >
                                {rowBusy ? (
                                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                ) : (
                                  <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                          </TooltipTrigger>
                          <TooltipContent>Remove</TooltipContent>
                        </Tooltip>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove attachment?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This removes {fileName} from the product attachment list. The file will no longer be available for this SKU.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel disabled={rowBusy}>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => void handleDelete(attachment)}
                              disabled={rowBusy}
                            >
                              {rowBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TooltipProvider>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
